import { Component, Input, OnInit, OnChanges, SimpleChanges, EventEmitter, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AccountFilter, AccountMonthlyBalance, BankStatementAccount, BankStatementData, BankTransactionsWithSummary, isInternalUser, User } from '@portal-workspace/grow-shared-library';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LooseCurrencyPipe } from '../../pipes/loose-currency.pipe';
import { DayEndBalanceChartComponent } from './day-end-balance-chart.component';
import { DecisionMetricsComponent } from './decision-metrics.component';
import { TransactionSummaryComponent } from './transaction-summary.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { FlexModule } from "@angular/flex-layout";
import { FullTransactionListComponent } from './full-transaction-list.component';
import { CategoriesTableComponent } from './categories-table.component';
import { MonthlyDebitsCreditsChartComponent } from './monthly-debits-credits-chart.component';
import { MonthlyDebitsCreditsTableComponent } from './monthly-debits-credits-table.component';
import { NonPreferredLenderSummaryComponent } from './non-preferred-lender-summary.component';
import { OverdrawnTableComponent } from './overdrawn-table.component';
import { TransactionTableComponent } from './transaction-table.component';
import { DishonoursTableComponent } from './dishonours-table.component';
import { MonthlyRevenueChartComponent } from './monthly-revenue-chart.component';
import { MonthlyRevenueTableComponent } from './monthly-revenue-table.component';
import { getUser } from '@portal-workspace/grow-ui-library';

@Component({
  selector: 'bank-statement-overview',
  templateUrl: './bank-statement-overview.component.html',
  styleUrls: ['./bank-statement-overview.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, NgClass, ExtendedModule, CategoriesTableComponent, MonthlyRevenueTableComponent, MonthlyRevenueChartComponent, DishonoursTableComponent, TransactionTableComponent, OverdrawnTableComponent, NonPreferredLenderSummaryComponent, MonthlyDebitsCreditsChartComponent, MonthlyDebitsCreditsTableComponent, FullTransactionListComponent, MatButtonModule, FormsModule, FlexModule, ReactiveFormsModule, MatCheckboxModule, TransactionSummaryComponent, DecisionMetricsComponent, DayEndBalanceChartComponent, LooseCurrencyPipe, MatMenuModule, MatIconModule]
})
export class BankStatementOverviewComponent implements OnInit, OnChanges {
  @Input({ required: true }) data!: BankStatementData;
  @Input({ required: false }) selectedAccounts: string[] = [];
  @Input({ required: false }) groupedAccounts?: Map<string, BankStatementAccount[]>;
  @Input({ required: false }) parentComponent?: any;
  @Input({ required: false }) requestHistoricalDataFn?: () => void;
  @Output() accountSelector = new EventEmitter();

  dataSource: MatTableDataSource<BankStatementAccount>;
  columnsToDisplay = ['icon', 'BankName', 'BSBAccNumber', 'AccHolder', 'AccName', 'currentBalance', 'TotalCredit', 'LoanCreditFlag', 'version', 'selector'];
  accountFilter: AccountFilter = {};
  expandedElement!: BankStatementAccount | null;
  transactionListSelectedAccounts: string[] = [];
  monthlyBalance: AccountMonthlyBalance | null = null;
  loggedInUser: User | null = getUser();
  isInternalUser = false;

  // Filtered data - calculated once when transactionListSelectedAccounts changes
  filteredTransactionsByCategory: any[] = [];
  filteredAccounts: BankStatementAccount[] = [];
  filteredSelectedAccounts: string[] = [];

  // Update filtered data - called only when transactionListSelectedAccounts changes
  private updateFilteredData(): void {
    if (!this.data) {
      this.filteredTransactionsByCategory = [];
      this.filteredAccounts = [];
      this.filteredSelectedAccounts = [];
      return;
    }
    
    // If no accounts selected, return all data (default behavior)
    if (!this.transactionListSelectedAccounts.length) {
      this.filteredTransactionsByCategory = this.data.transactionsByCategory || [];
      this.filteredAccounts = this.data.accounts || [];
      this.filteredSelectedAccounts = [];
      return;
    }
    
    // Filter accounts to selected versions
    const selectedAccountsSet = new Set(this.transactionListSelectedAccounts);
    this.filteredAccounts = this.data.accounts.filter(account => selectedAccountsSet.has(account.id));
    
    // Filter selected account IDs to only those present in filtered accounts
    const filteredAccountIds = new Set(this.filteredAccounts.map(a => a.id));
    this.filteredSelectedAccounts = this.transactionListSelectedAccounts.filter(id => filteredAccountIds.has(id));
    
    // Collect transactions from selected account versions
    const filteredCategories: any[] = [];
    this.filteredAccounts.forEach(account => {
      account.transactionsByCategory.forEach(category => {
        // Find or create category in filtered list
        let existingCategory = filteredCategories.find(c => c.category === category.category);
        if (!existingCategory) {
          existingCategory = {
            category: category.category,
            transactions: [],
            summary: {
              numcredit: 0,
              numdebit: 0,
              totalcredit: 0,
              totaldebit: 0,
            }
          };
          filteredCategories.push(existingCategory);
        }
        
        // Add transactions and update summary
        existingCategory.transactions.push(...category.transactions);
        existingCategory.summary.numcredit += category.summary.numcredit;
        existingCategory.summary.numdebit += category.summary.numdebit;
        existingCategory.summary.totalcredit += category.summary.totalcredit;
        existingCategory.summary.totaldebit += category.summary.totaldebit;
      });
    });
    
    this.filteredTransactionsByCategory = filteredCategories;
  }

  constructor(private formBuilder: FormBuilder, private cdr: ChangeDetectorRef) {
    this.dataSource = new MatTableDataSource<BankStatementAccount>([]);
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.init();
      this.updateFilteredData();
    }
    if (changes['selectedAccounts'] && !changes['selectedAccounts'].firstChange) {
      // When selectedAccounts changes (e.g., version selection), update transaction list
      this.transactionListSelectedAccounts = this.selectedAccounts;
      this.updateFilteredData();
      this.cdr.detectChanges();
    }
  }

  init() {
    this.dataSource.data = this.data?.accounts;
    this.initAccountFilter();
    this.data.accounts.forEach((account: BankStatementAccount) => {
      this.accountFilter[account.id].setValue(this.selectedAccounts.includes(account.id));
    })
    this.transactionListSelectedAccounts = this.selectedAccounts;
    this.cdr.detectChanges();
    this.isInternalUser = isInternalUser(this.loggedInUser);
  }

  initAccountFilter() {
    this.data.accounts.forEach((account: BankStatementAccount) => {
      this.accountFilter[account.id] = new FormControl<boolean>(false);
    })
  }

  isEmpty(obj: BankTransactionsWithSummary, table: 'overdrawn' | 'non-preferred-lender' | 'dishonours'): boolean {
    switch (table) {
      case 'non-preferred-lender':
        return !obj.transactions?.length && !obj.summary.numcredit && !obj.summary.numdebit;
      case 'overdrawn':
        return !obj.summary.numdebit;
      case 'dishonours':
        return !obj.summary.numdebit;
      default:
        return !obj.transactions?.length && !obj.summary.numcredit && !obj.summary.numdebit;
    }
  }

  getColumnTitles(column: string): string {
    const titles = {
      BankName: 'Bank Name',
      BSBAccNumber: 'BSB / Acc. Number',
      AccHolder: 'Acc. Holder',
      AccName: 'Acc. Name',
      currentBalance: 'Current Balance',
      TotalCredit: '$ Total Credit',
      LoanCreditFlag: 'Credit Product',
      version: 'Version',
      icon: '',
      selector: ''
    };
    return titles[column as keyof typeof titles] ?? column;
  }

  getColumnValues(element: BankStatementAccount, column: string): string {
    switch (column) {
      case 'BankName':
        return element.bankName || '';
      case 'BSBAccNumber':
        return element.bsbAccNumber || '';
      case 'AccHolder':
        return element.accountHolder || '';
      case 'AccName':
        return element.accountName || '';
      case 'currentBalance':
        return element.balance.current.toString() || '';
      case 'TotalCredit':
        return element.monthlyBalance?.totalcredit.toString() || '';
      case 'LoanCreditFlag':
        return element.isCreditProduct ? 'Yes' : 'No';
      default:
        return '';
    }
  }

  needCurrencyPipe = (column: string) =>
    ['currentBalance', 'availableBalance', 'TotalCredit'].includes(column);

  needAlignRight = (column: string) =>
    ['currentBalance', 'TotalCredit', 'LoanCreditFlag'].includes(column);

  onClickRow(element: BankStatementAccount) {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  onConfirmAccountSelection() {
    this.selectedAccounts = this.data.accounts.filter((account: BankStatementAccount) =>
      this.accountFilter[account.id].value
    ).map((account: BankStatementAccount) => account.id);
    this.transactionListSelectedAccounts = this.selectedAccounts;
    this.accountSelector.emit(this.selectedAccounts);
    console.log(this.selectedAccounts)
  }

  viewAccountTransactions(element: BankStatementAccount) {
    console.log(element);
    this.transactionListSelectedAccounts = [element.id];
    const el = document.getElementById("transaction-list");
    if (el) {
      el.scrollIntoView();
    }
  }

  // Delegate version-related methods to parent component if available
  hasMultipleVersions(account: BankStatementAccount): boolean {
    if (this.parentComponent?.hasMultipleVersions) {
      return this.parentComponent.hasMultipleVersions(account);
    }
    return false;
  }

  getVersionsForAccount(account: BankStatementAccount): BankStatementAccount[] {
    if (this.parentComponent?.getVersionsForAccount) {
      return this.parentComponent.getVersionsForAccount(account);
    }
    return [];
  }

  selectVersion(currentAccount: BankStatementAccount, selectedVersion: BankStatementAccount): void {
    if (this.parentComponent?.selectVersion) {
      this.parentComponent.selectVersion(currentAccount, selectedVersion);
    }
  }

  formatUploadDate(dateString?: string): string {
    if (this.parentComponent?.formatUploadDate) {
      return this.parentComponent.formatUploadDate(dateString);
    }
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  formatUploadTime(dateString?: string): string {
    if (this.parentComponent?.formatUploadTime) {
      return this.parentComponent.formatUploadTime(dateString);
    }
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  }

  getDays(account: BankStatementAccount): string {
    if (!account.firstDate || !account.lastDate) return '';
    
    const date1 = new Date(account.firstDate);
    const date2 = new Date(account.lastDate);
    const diffTime = date2.getTime() - date1.getTime();
    const days = Math.floor(diffTime / (1000 * 3600 * 24));
    
    return `${days} days`;
  }

  trackByFn(index: number, item: BankStatementAccount): string {
    return item.id;
  }

  // Check if historical data can be requested (hasHistoricalVersions flag is set and button hasn't been clicked yet)
  canRequestHistoricalData(): boolean {
    if (!this.data?.hasHistoricalVersions || !this.requestHistoricalDataFn) {
      return false;
    }
    return true;
  }

  // Request historical data from parent
  onRequestHistoricalData(): void {
    if (this.requestHistoricalDataFn) {
      this.requestHistoricalDataFn();
    }
  }
}
